import { Component, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTreeModule } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { OfferService } from '../../service/offer.service';
import { MatMenuModule } from '@angular/material/menu';
import { OfferWithImage } from '../../model/offer-with-image';
import { GraphqlService } from '@/service/graphql.service';
import { MatChipsModule } from '@angular/material/chips';
import { trigger, transition, style, animate } from '@angular/animations';
import { OfferCardComponent } from '@/app/components/offer-card/offer-card.component';
import { User } from '@/model/user';
import { ManagedUser } from '@/model/user';
import { UserService } from '@/service/user.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { getTagInfoFromUrlParams, TagInfo } from '@/utils/search-query-params';
interface SearchFilter {
  fulltext: string[]; // For free text search terms
  categories: string[]; // For category IDs
  offerTypes: string[]; // For offer type IDs
  postalCode: string[]; // For postal code filtering
}

interface FilterNode {
  id: string; // Add ID field
  name: string;
  children?: FilterNode[];
  selected?: boolean;
  type: string;
}

interface ExampleFlatNode {
  id: string; // Add ID field
  expandable: boolean;
  name: string;
  level: number;
  selected: boolean;
  type: string;
}

interface SearchProfile {
  id: string;
  name: string;
  postalCodes: string[];
  profilePicture: string | null;
  categories: { id: string; name: string }[];
}

const FILTER_DATA: FilterNode[] = [
  {
    id: 'categories',
    name: 'Kategorien',
    children: [],
    selected: false,
    type: 'category',
  },
  {
    id: 'offerTypes',
    name: 'Angebotsart',
    children: [],
    selected: false,
    type: 'offerType',
  },
];

@Component({
  selector: 'app-search-overview',
  imports: [
    CommonModule,
    MatCardModule,
    MatTreeModule,
    MatButtonModule,
    MatIcon,
    MatChipsModule,
    MatMenuModule,
    MatCheckboxModule,
    OfferCardComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
],
  templateUrl: './search-overview.component.html',
  styleUrls: ['./search-overview.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: true,
})
export class SearchOverviewComponent implements OnInit {
  private _transformer = (node: FilterNode, level: number) => {
    return {
      id: node.id,
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      selected: node.selected ?? false,
      type: node.type,
    };
  };

  treeControl = new FlatTreeControl<ExampleFlatNode>(
    (node) => node.level,
    (node) => node.expandable,
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  offers: OfferWithImage[] = [];
  selectedNode: ExampleFlatNode | null = null;
  tags: TagInfo[] = [];
  isLoading = false;
  searchFilter: SearchFilter = {
    fulltext: [],
    categories: [],
    offerTypes: [],
    postalCode: [],
  };

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  public searchProfiles: SearchProfile[] = [];
  public selectedSearchProfile: SearchProfile | null = null;
  public selectedProfileId = 'individual';

  constructor(
    private route: ActivatedRoute,
    private offerService: OfferService,
    private graphqlService: GraphqlService,
    private router: Router,
    private userService: UserService,
  ) {
    this.dataSource.data = FILTER_DATA;
  }

  hasChild = (_: number, node: ExampleFlatNode) => node.expandable;

  ngOnInit(): void {
    // Explicitly initialize profile selection to 'individual'
    this.selectedProfileId = 'individual';
    this.selectedSearchProfile = null;

    this.route.queryParams.subscribe((params: Params) => {
      this.tags = getTagInfoFromUrlParams(params);

      this.searchOffersByTags();
      // Update filter selections based on tags
      this.updateFilterSelections();
    });

    // Load categories
    this.graphqlService.allCategories().subscribe((data) => {
      FILTER_DATA[0].children = data.map((category) => ({
        id: category.id,
        name: category.name,
        selected: this.tags.some(
          (t) => t.id === category.id && t.type === 'category',
        ),
        type: 'category',
      }));
      this.updateDataSource();
    });

    // Load offer types
    this.offerService.getAllOfferTypes().subscribe((types) => {
      FILTER_DATA[1].children = types.map((type) => ({
        id: type.id,
        name: type.offerName,
        selected: this.tags.some(
          (t) => t.id === type.id && t.type === 'offerType',
        ),
        type: 'offerType',
      }));
      this.updateDataSource();
    });

    this.userService.getUserInfo().subscribe((user) => {
      this.updateSearchProfiles(user);
    });

    this.userService.getManagedUsers().subscribe((managedUsers) => {
      this.updateSearchProfiles(managedUsers);
    });
  }

  private updateSearchProfiles(user: User | ManagedUser[]): void {
    if (!user) return;

    this.searchProfiles = [
      ...this.searchProfiles,
      ...(Array.isArray(user) ? user : [user]).map((user) => ({
        id: user.id || '',
        name: `${user.firstName} ${user.lastName}`,
        postalCodes: user.zipCode ? [user.zipCode] : [],
        profilePicture: user.profilePicture?.url || null,
        categories: user.categories || [],
      })),
    ];
  }

  onProfileSelect(profile: SearchProfile | null): void {
    // Set the selected profile (for UI indication)
    this.selectedSearchProfile = profile;

    // Update the selectedProfileId
    if (profile === null) {
      this.selectedProfileId = 'individual';
    } else {
      this.selectedProfileId = profile.id;
    }

    if (!profile) {
      // Clear postal code tags and category tags if null was selected
      this.tags = this.tags.filter(
        (t) => t.type !== 'postalCode' && t.type !== 'category',
      );
      this.updateUrlWithTags();
      return;
    }

    // Clear existing postal codes and categories
    this.tags = this.tags.filter(
      (t) => t.type !== 'postalCode' && t.type !== 'category',
    );

    // Add profile's postal codes as tags
    if (profile.postalCodes && profile.postalCodes.length > 0) {
      profile.postalCodes.forEach((code) => {
        this.tags.push({ id: code, name: code, type: 'postalCode' });
      });
    }

    // Add profile's categories as tags
    if (profile.categories && profile.categories.length > 0) {
      profile.categories.forEach((category) => {
        if (
          !this.tags.some((t) => t.id === category.id && t.type === 'category')
        ) {
          this.tags.push({
            id: category.id,
            name: category.name,
            type: 'category',
          });
        }
      });
    }

    // Update URL params
    this.updateUrlWithTags();
  }

  private updateFilterSelections(): void {
    // Update category selections
    if (FILTER_DATA[0].children) {
      FILTER_DATA[0].children = FILTER_DATA[0].children.map((child) => ({
        ...child,
        selected: this.tags.some(
          (tag) => tag.id === child.id && tag.type === 'category',
        ),
      }));
    }

    // Update offer type selections
    if (FILTER_DATA[1].children) {
      FILTER_DATA[1].children = FILTER_DATA[1].children.map((child) => ({
        ...child,
        selected: this.tags.some(
          (tag) => tag.id === child.id && tag.type === 'offerType',
        ),
      }));
    }

    this.updateDataSource();
  }

  private updateDataSource(): void {
    this.dataSource.data = FILTER_DATA;
  }

  getSelectedCount(type: string): number {
    return this.tags.filter((tag) => tag.type === type).length;
  }

  private updateSearchFilter(): void {
    // Reset the filter
    this.searchFilter = {
      fulltext: [],
      categories: [],
      offerTypes: [],
      postalCode: [],
    };

    // Categorize each tag based on its type
    this.tags.forEach((tag) => {
      switch (tag.type) {
        case 'category':
          this.searchFilter.categories.push(tag.id); // Use ID instead of name
          break;
        case 'offerType':
          this.searchFilter.offerTypes.push(tag.id); // Use ID instead of name
          break;
        case 'postalCode':
          this.searchFilter.postalCode.push(tag.id); // Use postal code as ID
          break;
        case 'custom':
        case 'offer':
          this.searchFilter.fulltext.push(tag.name);
          break;
        default:
          this.searchFilter.fulltext.push(tag.name);
          break;
      }
    });

    // We no longer need to handle selectedPostalCodes separately
    // since we're using only tags for postal codes
  }

  searchOffersByTags(): void {
    this.isLoading = true;
    this.updateSearchFilter(); // Update the structured filter before searching

    // Use the new structured filter
    this.offerService
      .searchOffersByStructuredFilter({
        fulltext: this.searchFilter.fulltext,
        categories: this.searchFilter.categories,
        offerTypes: this.searchFilter.offerTypes,
        postalCode: this.searchFilter.postalCode,
        limit: 20,
        offset: 0,
      })
      .subscribe({
        next: (data) => {
          this.offers = data;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Fehler bei der Suche nach Angeboten:', error);
          this.isLoading = false;
        },
      });
  }

  stripHtmlTags(description: string): string {
    return description.replace(/<[^>]*>/g, '');
  }

  onRemoveTag(tag: TagInfo): void {
    this.tags = this.tags.filter(
      (t) => !(t.id === tag.id && t.type === tag.type),
    );
    this.updateSearchFilter();

    // Clear selected profile when removing postal code or category tag
    if (tag.type === 'postalCode' || tag.type === 'category') {
      // Only reset if we actually have a profile selected and this tag came from the profile
      if (this.selectedSearchProfile) {
        // For categories, check if it's from the selected profile
        if (
          tag.type === 'category' &&
          this.selectedSearchProfile.categories.some((c) => c.id === tag.id)
        ) {
          this.selectedSearchProfile = null;
          this.selectedProfileId = 'individual';
        }
        // For postal codes, always reset (existing behavior)
        else if (tag.type === 'postalCode') {
          this.selectedSearchProfile = null;
          this.selectedProfileId = 'individual';
        }
      }
    }

    const queryParams =
      this.tags.length > 0
        ? {
            tags: this.tags.map((t) => `${t.id}:${t.name}:${t.type}`).join(','),
          }
        : {};
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  getTagColorClass(type: string): string {
    switch (type) {
      case 'category':
        return 'bg-green-100 text-green-700';
      case 'offerType':
        return 'bg-blue-100 text-blue-700';
      case 'location':
        return 'bg-yellow-100 text-yellow-700';
      case 'postalCode':
        return 'bg-purple-100 text-purple-700';
      default:
        return '';
    }
  }

  onCategoryToggle(node: ExampleFlatNode): void {
    node.selected = !node.selected;

    if (node.selected) {
      if (!this.tags.some((t) => t.id === node.id && t.type === node.type)) {
        this.tags.push({ id: node.id, name: node.name, type: node.type });
      }
    } else {
      this.tags = this.tags.filter(
        (t) => !(t.id === node.id && t.type === node.type),
      );
    }

    this.updateSearchFilter();

    const queryParams =
      this.tags.length > 0
        ? {
            tags: this.tags.map((t) => `${t.id}:${t.name}:${t.type}`).join(','),
          }
        : {};

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  // New method to add postal code from input in top tags area
  addPostalCodeFromInput(value: string): void {
    if (!value || value.trim() === '') return;

    const trimmedValue = value.trim();

    // Add postal code as a tag to be included in URL
    if (
      !this.tags.some((t) => t.id === trimmedValue && t.type === 'postalCode')
    ) {
      this.tags.push({
        id: trimmedValue,
        name: trimmedValue,
        type: 'postalCode',
      });

      // Update URL params
      this.updateUrlWithTags();
    }
  }

  // These methods are no longer needed since we're using only tags
  // for postal codes, but we'll keep them defined to avoid breaking
  // any existing code that might still call them

  addPostalCode(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.addPostalCodeFromInput(value);
    }
    // Clear the input value
    event.chipInput!.clear();
  }

  removePostalCode(postalCode: string): void {
    // Just remove from tags
    this.tags = this.tags.filter(
      (t) => !(t.id === postalCode && t.type === 'postalCode'),
    );
    // Update URL params
    this.updateUrlWithTags();
  }

  editPostalCode(postalCode: string, event: MatChipEditedEvent): string[] {
    const value = event.value.trim();

    // Remove if empty
    if (!value) {
      this.removePostalCode(postalCode);
      return [];
    }

    // Update tag
    const tagIndex = this.tags.findIndex(
      (t) => t.id === postalCode && t.type === 'postalCode',
    );
    if (tagIndex >= 0) {
      this.tags[tagIndex] = { id: value, name: value, type: 'postalCode' };
      // Update URL params
      this.updateUrlWithTags();
    }

    return [];
  }

  // Helper method to update URL with tags
  private updateUrlWithTags(): void {
    const queryParams =
      this.tags.length > 0
        ? {
            tags: this.tags.map((t) => `${t.id}:${t.name}:${t.type}`).join(','),
          }
        : {};

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });

    // Trigger search with updated filters
    this.searchOffersByTags();
  }

  // Compare function for mat-select to handle both objects and null values
  compareProfiles(
    profile1: SearchProfile | null,
    profile2: SearchProfile | null,
  ): boolean {
    if (profile1 === null && profile2 === null) {
      return true;
    }
    if (!profile1 || !profile2) {
      return false;
    }
    return profile1.id === profile2.id;
  }

  // New string-based method for profile selection
  onProfileIdSelect(profileId: string): void {
    this.selectedProfileId = profileId;

    if (profileId === 'individual') {
      // Handle the "Individual" selection
      this.selectedSearchProfile = null;

      // Clear postal code and category tags
      this.tags = this.tags.filter(
        (t) => t.type !== 'postalCode' && t.type !== 'category',
      );
      this.updateUrlWithTags();
      return;
    }

    // Find the profile from the ID
    const profile = this.searchProfiles.find((p) => p.id === profileId);
    if (profile) {
      this.selectedSearchProfile = profile;

      // Clear existing postal codes and categories
      this.tags = this.tags.filter(
        (t) => t.type !== 'postalCode' && t.type !== 'category',
      );

      // Add profile's postal codes as tags
      if (profile.postalCodes && profile.postalCodes.length > 0) {
        profile.postalCodes.forEach((code) => {
          this.tags.push({ id: code, name: code, type: 'postalCode' });
        });
      }

      // Add profile's categories as tags
      if (profile.categories && profile.categories.length > 0) {
        profile.categories.forEach((category) => {
          if (
            !this.tags.some(
              (t) => t.id === category.id && t.type === 'category',
            )
          ) {
            this.tags.push({
              id: category.id,
              name: category.name,
              type: 'category',
            });
          }
        });
      }

      // Update URL params
      this.updateUrlWithTags();
    }
  }
}
